/**
 * Admin-defined custom document labels ("+ New label" in the tenant Documents
 * tab). `tenant_documents.document_type` stays a closed set — a DB trigger
 * (see the tenant_document_custom_labels migration) rejects any value that
 * isn't one of the built-ins or a slug from this table — so a label created
 * here is reusable across every tenant, not a one-off free-text tag.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export type TenantDocumentLabel = {
  id: string;
  slug: string;
  label: string;
};

export function useTenantDocumentLabels() {
  return useQuery<TenantDocumentLabel[]>({
    queryKey: ["v2", "tenant-document-labels"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("tenant_document_labels")
        .select("id, slug, label")
        .order("created_at", { ascending: true });
      if (error) throw error;
      return (data ?? []) as TenantDocumentLabel[];
    },
  });
}

/** Slugify a label into a stable document_type value, e.g. "Key list" -> "custom_key_list". */
function slugify(label: string): string {
  const base = label
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
  return `custom_${base || "label"}`;
}

export function useCreateTenantDocumentLabel() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (label: string) => {
      const slug = slugify(label);
      const { data, error } = await supabase
        .from("tenant_document_labels")
        .insert({ slug, label: label.trim() })
        .select("id, slug, label")
        .single();
      if (error) throw error;
      return data as TenantDocumentLabel;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ["v2", "tenant-document-labels"],
      });
    },
  });
}
